import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Message, SpinnerService } from 'shared-components';
import { CultivosService } from '../../../services/cultivos.service';

@Component({
  selector: 'app-pro-new-edit',
  templateUrl: './new-edit.component.html'
})
export class ProductoresNewEditComponent implements OnInit, OnDestroy {

  private _productor;
  private readonly destroy$ = new Subject<void>();
  private _provincia;
  public applicationUserId;
  public idiomas;
  public newEditForm: UntypedFormGroup;
  public showError: boolean;
  public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
  public mensajes = new Array<Message>();
  public toCloseSpinner = 0;

  private _filtersDropDown;
  public paises;
  public paisesProductor;
  public provinciasProductor;
  public municipiosProductor;
  public disabledPais: Subject<boolean> = new Subject();
  public disabledPaisProductor: Subject<boolean> = new Subject();
  public disabledProvinciaProductor: Subject<boolean> = new Subject();
  public disabledMunicipioProductor: Subject<boolean> = new Subject();

  @Input() productorId;
  @Output() refresh = new EventEmitter<boolean>();
  @Output() mensaje = new EventEmitter<string>();

  constructor(
    private _fb: UntypedFormBuilder,
    private readonly _sessionContext: SessionContextService,
    private readonly _spinnerService: SpinnerService,
    private readonly _cultivosService: CultivosService,
    private readonly _dataProvider: DataProvider) { }

  ngOnInit() {
    this.newEditForm = this._fb.group({
      nombre: ['', Validators.required],
      denominacion: ['', Validators.required],
      pais: [{value:'', disabled: true }],
      nifcif: [''],
      paisProductor: [''],
      provinciaProductor: [{value:'', disabled: true }],
      municipioProductor: [{value:'', disabled: true }],
      personaContacto: [''],
      idioma: [''],
      userName: ['', Validators.pattern('^[A-z0-9._%+-]+@[A-z0-9.-]+\\.[a-z]{2,4}$')],
      password: [''],
      telefono: [''],
      direccion: [''],
      dobleFactor: ['']
    });

    this.paisesProductor = this._dataProvider.paises;

    if (this.productorId) {

      this.newEditForm.get('denominacion').clearValidators();
      this.newEditForm.get('denominacion').updateValueAndValidity();

      const promises: Promise<any>[] = [];
      const promisesPais: Promise<any>[] = [];

      this._spinnerService.showSpinner();
      promises.push(
        this.getProductor()
      );

      Promise.all(promises).then(
        (values) => {
          promisesPais.push(
            this.getProvinciasPais()
          );

          promisesPais.push(
            this.getMunicipiosProvincia()
          );

          Promise.all(promisesPais).then(
            () => {
              this.newEditForm.patchValue({
                provinciaProductor: this.provinciasProductor.find(x => x.id === this._productor.provinciaId) ?
                                    this.provinciasProductor.find(x => x.id === this._productor.provinciaId) : '',
                municipioProductor: this.municipiosProductor.find(x => x.id === this._productor.municipioId) ?
                                    this.municipiosProductor.find(x => x.id === this._productor.municipioId) : '',
              });

              this._filtersDropDown = {
                municipioId: this._productor.municipioId
              };
              return true;
            }, (error) => {
              console.log('Promise rejected with ' + JSON.stringify(error));
            }
          ).catch(e => console.log(e));
        }, (error) => {
          console.log('Promise rejected with ' + JSON.stringify(error));
        }
      ).catch(e => console.log(e));
    }

    this.newEditForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
      if (denominacion) {
        this._filtersDropDown = {
          denominacionId: denominacion.id
        };

        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._cultivosService.getPaisesDenominacion(this._filtersDropDown).toPromise().finally(() => {
          this.checkCloseSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.paises = resp.lista;
            this.newEditForm.get('pais').setValidators(Validators.required);
            this.newEditForm.get('pais').enable();
            this.newEditForm.get('pais').updateValueAndValidity();
            this.disabledPais.next(false);
          }
        });
      } else {
        this.paises = null;
        this.disabledPais.next(true);
        this.newEditForm.get('pais').reset();
      }
    });

    this.newEditForm.get('paisProductor').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((paisP) => {
      if (paisP) {
        this._filtersDropDown = {
          paisId: paisP.id
        };

        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._cultivosService.getProvinciasPais(this._filtersDropDown).toPromise().finally(() => {
          this.checkCloseSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.provinciasProductor = resp.lista;
            if (this._provincia) {
              this.newEditForm.patchValue({
                provinciaProductor: this.provinciasProductor.find(x => x.id === this._provincia.id) ?
                                    this.provinciasProductor.find(x => x.id === this._provincia.id) : '',
              });
              this._provincia = null;
            }
            this.newEditForm.get('provinciaProductor').setValidators(Validators.required);
            this.newEditForm.get('provinciaProductor').enable();
            this.newEditForm.get('provinciaProductor').updateValueAndValidity();
            this.disabledProvinciaProductor.next(false);
          }
        });
      } else {
        this.provinciasProductor = null;
        this.municipiosProductor = null;
        this.disabledProvinciaProductor.next(true);
        this.disabledMunicipioProductor.next(true);
        this.newEditForm.get('municipioProductor').reset();
        this.newEditForm.get('provinciaProductor').reset();
      }
    });

    this.newEditForm.get('provinciaProductor').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((provinciaP) => {
      if (provinciaP) {
        this._filtersDropDown = {
          provinciaId: provinciaP.id
        };

        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._cultivosService.getMunicipiosProvincia(this._filtersDropDown).toPromise().finally(() => {
          this.checkCloseSpinner();
        }).then((resp) => {
          if (resp.lista.length > 0) {
            this.municipiosProductor = resp.lista;
            this.newEditForm.get('municipioProductor').setValidators(Validators.required);
            this.newEditForm.get('municipioProductor').enable();
            this.newEditForm.get('municipioProductor').updateValueAndValidity();
            this.disabledMunicipioProductor.next(false);
          }
        });
      } else {
        this.municipiosProductor = null;
        this.disabledMunicipioProductor.next(true);
        this.newEditForm.get('municipioProductor').reset();
      }
    });

    this.getIdiomas();
    if (this.productorId) {
      this.getProductor();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public get role() {
    return this._sessionContext.sessionUser.role;
  }

  public get denominaciones() {
    return this._dataProvider.denominaciones;
  }

  public get isSmallScreen() {
    return this._sessionContext.isSmallScreen();
  }

  public getIdiomas() {
    this._spinnerService.showSpinner();
    this.toCloseSpinner += 1;
    this._cultivosService.getIdiomas().toPromise().finally(() => {
      this.checkCloseSpinner();
    }).then((resp) => {
      this.idiomas = resp;
    });
  }

  public getProductor(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._cultivosService.getProductor(this.productorId).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then((resp) => {
        this._productor = resp;
        this.newEditForm.patchValue({
          nombre: resp.des,
          paisProductor: this.paisesProductor.find(x => x.id === resp.paisId) ? this.paisesProductor.find(x => x.id === resp.paisId) : '',
          nifcif: resp.nifcif ? resp.nifcif : '',
          telefono: resp.phoneNumber ? resp.phoneNumber : '',
          personaContacto: resp.personaContacto ? resp.personaContacto : '',
          userName: resp.userName ? resp.userName : ''
        });

        if (resp.userName) {
          this.newEditForm.get('userName').disable();
          this.newEditForm.get('userName').clearValidators();
          this.newEditForm.get('userName').updateValueAndValidity();
          this.newEditForm.get('password').disable();
          this.newEditForm.get('password').clearValidators();
          this.newEditForm.get('password').updateValueAndValidity();
        }
        setTimeout(resolve, 1);
       }, (error) => {
          this.showApiError = true;
          if (error.status === 420) {
            this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
          } else {
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
          }
       });
    });
  }

  public getProvinciasPais(): Promise<any> {
    return new Promise((resolve, reject) => {
      this._filtersDropDown = {
        paisId: this._productor.paisId
      };

      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._cultivosService.getProvinciasPais(this._filtersDropDown).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then((resp) => {
        if (resp.lista.length > 0) {
          this.provinciasProductor = resp.lista;
          this.newEditForm.get('municipioProductor').setValidators(Validators.required);
          this.newEditForm.get('municipioProductor').enable();
          this.newEditForm.get('municipioProductor').updateValueAndValidity();
          this.disabledProvinciaProductor.next(false);
        }
        setTimeout(resolve, 1);
      });
    });
  }

  public getMunicipiosProvincia() {
    return new Promise((resolve, reject) => {
      this._filtersDropDown = {
        provinciaId: this._productor.provinciaId
      };

      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._cultivosService.getMunicipiosPais(this._filtersDropDown).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then((resp) => {
        if (resp.lista.length > 0) {
          this.municipiosProductor = resp.lista;
        }
        setTimeout(resolve, 1);
      });
    });
  }

  public close() {
    this.refresh.emit(false);
  }

  public save() {
    this.checkForm();
    if (this.newEditForm.valid && !this.productorId) {
      this._productor = {
        usuarioProductor: {
          productor: {
            des: this.newEditForm.get('nombre').value,
            municipioId: this.newEditForm.get('municipioProductor').value ? this.newEditForm.get('municipioProductor').value.id : null,
            nifcif: this.newEditForm.get('nifcif').value ? this.newEditForm.get('nifcif').value : '',
            personaContacto: this.newEditForm.get('personaContacto').value ? this.newEditForm.get('personaContacto').value : '',
          },
          applicationUser: this.getApplicationUser(),
          productorPaisDenominacion: {
            productorId: 0,
            paisId: this.newEditForm.get('pais').value ? this.newEditForm.get('pais').value.id : null,
            denominacionId: this.newEditForm.get('denominacion').value ? this.newEditForm.get('denominacion').value.id : null,
          }
        }
      };
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._cultivosService.createProductor(this._productor).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then((resp) => {
        if (resp.id) {
          this.refresh.emit(true);
          this.mensaje.emit('global.success.itemCreated');
        } else {
          this.showError = true;
          this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        }
      });
    } else if (this.newEditForm.valid && this.productorId) {
      this._productor = {
        id: this.productorId,
        des: this.newEditForm.get('nombre').value,
        municipioId: this.newEditForm.get('municipioProductor').value ? this.newEditForm.get('municipioProductor').value.id : null,
        nifcif: this.newEditForm.get('nifcif').value ? this.newEditForm.get('nifcif').value : '',
        personaContacto: this.newEditForm.get('personaContacto').value ? this.newEditForm.get('personaContacto').value : '',
      };
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._cultivosService.updateProductor(this._productor).toPromise().finally(() => {
        this.checkCloseSpinner();
      }).then(
        (resp) => {
          this.refresh.emit(true);
          this.mensaje.emit('global.success.itemUpdated');
        },
        (error) => {
          this.showError = true;
          this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        }
      );
    }
  }

  private checkForm() {
    this.showError = this.newEditForm.invalid;
    if (this.showError) {
      this.mensajes.push({ class: 'error', message: 'global.error.required' });
    }
  }

  private checkCloseSpinner() {
    this.toCloseSpinner -= 1;
    if (this.toCloseSpinner === 0) {
      this._spinnerService.hideSpinner();
    }
  }

  public getApplicationUser() {
    let applicationUser;
    if (this.role === 'Auditor') {
      applicationUser = null;
    } else {
      applicationUser = {
        userName: this.newEditForm.get('userName').value ? this.newEditForm.get('userName').value : null,
        password: this.newEditForm.get('password').value,
        phoneNumber: this.newEditForm.get('telefono').value ? this.newEditForm.get('telefono').value : '',
        useTwoFactor: this.newEditForm.get('dobleFactor').value ? this.newEditForm.get('dobleFactor').value : false,
        idiomaId: this.newEditForm.get('idioma').value ? this.newEditForm.get('idioma').value.idiomaId : '',
        nombre: this.newEditForm.get('nombre').value,
        activo: true
      };
    }
    return applicationUser;
  }
}
